/**
 * Convertisseurs Firestore : les Timestamp deviennent des Date, les types métier
 * n'exposent que des Date (CLAUDE.md, piège 8). Lecture seule : les écritures
 * passent par les fonctions de services/books.ts.
 */
import {
  Timestamp,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
} from 'firebase/firestore'
import type { Book, Loan, LoanState } from '@/types'

export function toDate(v: unknown): Date | undefined {
  if (v instanceof Timestamp) return v.toDate()
  if (v instanceof Date) return v
  if (typeof v === 'string') {
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? undefined : d
  }
  return undefined
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined)
const num = (v: unknown): number | undefined => (typeof v === 'number' && v !== 0 ? v : undefined)

function toLoanState(v: unknown): LoanState {
  const d = (v ?? {}) as DocumentData
  return {
    status: d.status === 'borrowed' ? 'borrowed' : 'available',
    loanId: str(d.loanId),
    borrowerUid: str(d.borrowerUid),
    borrowerName: str(d.borrowerName),
    external: d.external === true || undefined,
    borrowedAt: toDate(d.borrowedAt),
  }
}

// Les serverTimestamp en attente d'écriture sont estimés localement.
const readOptions = (options: SnapshotOptions): SnapshotOptions => ({ ...options, serverTimestamps: 'estimate' })

export const bookConverter: FirestoreDataConverter<Book> = {
  toFirestore(): DocumentData {
    throw new Error('Écriture via bookConverter non prévue : utiliser services/books.ts')
  },
  fromFirestore(snap: QueryDocumentSnapshot, options: SnapshotOptions): Book {
    const d = snap.data(readOptions(options))
    return {
      id: snap.id,
      title: typeof d.title === 'string' ? d.title : '',
      authors: Array.isArray(d.authors) ? d.authors.filter((a): a is string => typeof a === 'string') : [],
      publisher: str(d.publisher),
      year: num(d.year),
      isbn: str(d.isbn),
      googleBooksId: str(d.googleBooksId),
      description: str(d.description),
      thumbnailUrl: str(d.thumbnailUrl),
      location: typeof d.location === 'string' ? d.location : '',
      personalNotes: str(d.personalNotes),
      cardImagePath: str(d.cardImagePath),
      loan: toLoanState(d.loan),
      createdAt: toDate(d.createdAt) ?? new Date(0),
      updatedAt: toDate(d.updatedAt) ?? new Date(0),
      createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
    }
  },
}

export const loanConverter: FirestoreDataConverter<Loan> = {
  toFirestore(): DocumentData {
    throw new Error('Les prêts sont écrits uniquement par l’API')
  },
  fromFirestore(snap: QueryDocumentSnapshot, options: SnapshotOptions): Loan {
    const d = snap.data(readOptions(options))
    return fromLoanJSON({ ...d, id: snap.id })
  },
}

/** Convertit un prêt reçu de l'API (dates RFC 3339) ou de Firestore. */
export function fromLoanJSON(d: DocumentData): Loan {
  return {
    id: str(d.id),
    bookId: String(d.bookId ?? ''),
    bookTitle: String(d.bookTitle ?? ''),
    borrowerUid: str(d.borrowerUid),
    borrowerName: String(d.borrowerName ?? ''),
    external: d.external === true,
    recordedBy: String(d.recordedBy ?? ''),
    borrowedAt: toDate(d.borrowedAt) ?? new Date(0),
    returnedAt: toDate(d.returnedAt),
    returnedBy: str(d.returnedBy),
  }
}
